import { Button, Card, Drawer, formatDate, formatTime, useToast } from "@helmr/ui";
import { useQuery } from "@tanstack/react-query";
import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { createAppointment, type NewAppointmentKind } from "../../api/appointments";
import { isApiError } from "../../api/client";
import { createLead, listLeads, patchLead, type Lead } from "../../api/leads";
import { useServices } from "../../app/services";
import { NoAccess } from "../../app/NoAccess";
import { localDateOf } from "@helmr/contracts";
import { zonedToInstant } from "../../lib/zoned";
import { useInvalidateLeadData, useScope, type Scope } from "./hooks";
import { leads as t } from "./strings";
import { leadName } from "./ui";
import s from "./leads.module.css";

type Kind = "trial" | NewAppointmentKind;
const KINDS: Kind[] = ["trial", "assessment", "tour", "consultation"];

const nextHour = (nowMs: number, tz: string) => {
  const p = new Intl.DateTimeFormat("en-GB", { timeZone: tz, hourCycle: "h23", hour: "2-digit" }).formatToParts(nowMs);
  const h = Number(p.find((x) => x.type === "hour")?.value ?? 9);
  return `${String(Math.min(h + 1, 23)).padStart(2, "0")}:00`;
};

/**
 * Book a trial, assessment, tour or consultation. A trial is `PATCH /leads/:id { trialAt }`; the others are
 * `POST /appointments`. With no lead given, the person is found by search or created first (source `walk_in`).
 */
export function BookTrialForm({ sc, lead, onBooked, onCancel }: { sc: Scope; lead?: Lead; onBooked: (lead: Lead, when: number) => void; onCancel?: () => void }) {
  const { client } = useServices();
  const toast = useToast();
  const refresh = useInvalidateLeadData();
  const tz = sc.ctx.timeZone;
  const [kind, setKind] = useState<Kind>("trial");
  const [date, setDate] = useState(sc.date);
  const [time, setTime] = useState(nextHour(sc.nowMs, tz));
  const [mode, setMode] = useState<"existing" | "new">("existing");
  const [picked, setPicked] = useState<Lead | null>(lead ?? null);
  const [search, setSearch] = useState("");
  const [name, setName] = useState("");
  const [phone, setPhone] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const term = search.trim();
  const hits = useQuery({ queryKey: ["lead-search", ...sc.scopeKey, term], queryFn: () => listLeads(client, { q: term }, null, 8), enabled: !lead && mode === "existing" && !picked && term.length >= 2, retry: false });

  async function submit(e: React.FormEvent) {
    e.preventDefault();
    if (busy) return;
    if (!date || !time) { setError(t.trial.dateRequired); return; }
    if (mode === "existing" && !picked) { setError(t.trial.needLead); return; }
    if (mode === "new" && !phone.trim()) { setError(t.new.phoneRequired); return; }
    const startsAt = zonedToInstant(date, time, tz);
    setBusy(true);
    setError(null);
    try {
      let target = picked;
      if (!target) {
        const branchId = sc.branchId ?? sc.ctx.me?.branch?.id;
        if (!branchId) { setError(t.new.branchRequired); setBusy(false); return; }
        target = (await createLead(client, { phone: phone.trim(), ...(name.trim() ? { name: name.trim() } : {}), source: "walk_in", branchId })).lead;
      }
      // The API keeps a done trial's outcome across a new `trialAt` (and a no-show if the instant is unchanged): this is a new booking.
      if (kind === "trial") await patchLead(client, target.id, { trialAt: new Date(startsAt).toISOString(), ...(target.trialDoneAt ? { trialDoneAt: null } : {}), ...(target.trialNoShowAt ? { trialNoShowAt: null } : {}) });
      else await createAppointment(client, { kind, leadId: target.id, startsAt, ...(target.branchId ? { branchId: target.branchId } : {}) });
      await refresh();
      toast.show(t.trial.booked(t.trial.kinds[kind], `${formatDate(localDateOf(startsAt, tz), sc.ctx.locale, "weekday")} ${formatTime(startsAt, tz)}`));
      onBooked(target, startsAt);
    } catch (err) {
      setError(isApiError(err) ? err.message : t.trial.failed);
    } finally {
      setBusy(false);
    }
  }

  return (
    <form className={s.form} onSubmit={submit} noValidate id="book-form">
      <div className={s.field}>
        <label htmlFor="bk-kind">{t.trial.kind}</label>
        <select id="bk-kind" value={kind} onChange={(e) => setKind(e.target.value as Kind)}>
          {KINDS.map((k) => <option key={k} value={k}>{t.trial.kinds[k]}</option>)}
        </select>
      </div>
      <div className={s.field} style={{ gridTemplateColumns: "1fr 1fr", gap: 8 }}>
        <div className={s.field}><label htmlFor="bk-date">{t.trial.date}</label><input id="bk-date" type="date" value={date} onChange={(e) => setDate(e.target.value)} required /></div>
        <div className={s.field}><label htmlFor="bk-time">{t.trial.time}</label><input id="bk-time" type="time" value={time} onChange={(e) => setTime(e.target.value)} required /></div>
      </div>
      <p className={s.hint}>{t.trial.note}</p>

      {!lead ? (
        <>
          <div role="group" aria-label="Who" style={{ display: "flex", gap: 8 }}>
            <Button size="sm" variant={mode === "existing" ? "primary" : "default"} aria-pressed={mode === "existing"} onClick={() => setMode("existing")}>{t.trial.whoExisting}</Button>
            <Button size="sm" variant={mode === "new" ? "primary" : "default"} aria-pressed={mode === "new"} onClick={() => { setMode("new"); setPicked(null); }}>{t.trial.whoNew}</Button>
          </div>
          {mode === "existing" ? (
            picked ? (
              <div className={s.field}><span>{t.trial.picked(leadName(picked))}</span><div><Button size="sm" onClick={() => setPicked(null)}>{t.trial.change}</Button></div></div>
            ) : (
              <div className={s.field}>
                <label htmlFor="bk-search">{t.trial.search}</label>
                <input id="bk-search" type="search" value={search} onChange={(e) => setSearch(e.target.value)} autoComplete="off" />
                {hits.data && hits.data.leads.length === 0 ? <small>{t.trial.noResults}</small> : null}
                {hits.data && hits.data.leads.length > 0 ? (
                  <div className={s.pickList} role="listbox" aria-label={t.trial.whoExisting}>
                    {hits.data.leads.map((l) => <button type="button" role="option" aria-selected="false" key={l.id} onClick={() => setPicked(l)}><span>{leadName(l)}</span><span className={s.muted}>{l.phone}</span></button>)}
                  </div>
                ) : null}
              </div>
            )
          ) : (
            <>
              <div className={s.field}><label htmlFor="bk-name">{t.trial.name}</label><input id="bk-name" value={name} onChange={(e) => setName(e.target.value)} maxLength={120} autoComplete="off" /></div>
              <div className={s.field}><label htmlFor="bk-phone">{t.trial.phone}</label><input id="bk-phone" type="tel" inputMode="tel" value={phone} onChange={(e) => setPhone(e.target.value)} maxLength={40} autoComplete="off" /></div>
            </>
          )}
        </>
      ) : null}

      {error ? <p className={s.err} role="alert">{error}</p> : null}
      <div className={s.formActions}>
        <Button variant="primary" type="submit" busy={busy} busyLabel={t.trial.booking}>{t.trial.book}</Button>
        {onCancel ? <Button onClick={onCancel} disabled={busy}>{t.new.cancel}</Button> : null}
      </div>
    </form>
  );
}

/** From a lead's profile. */
export function BookTrialDrawer({ sc, lead, open, onClose }: { sc: Scope; lead: Lead; open: boolean; onClose: () => void }) {
  return (
    <Drawer open={open} onOpenChange={(o) => { if (!o) onClose(); }} title={t.trial.drawerTitle(leadName(lead))}>
      <div className={s.drawerPad}><BookTrialForm sc={sc} lead={lead} onBooked={onClose} onCancel={onClose} /></div>
    </Drawer>
  );
}

/** `/leads/trial`: Home's "Book trial" quick action. */
export function BookTrialPage() {
  const sc = useScope();
  const nav = useNavigate();
  if (sc.ctx.status !== "loading" && !sc.ctx.has("leads.edit")) return <NoAccess />;
  return (
    <div className={s.page}>
      <title>Book a trial · Helmr</title>
      <div className={s.head}><div><h1 tabIndex={-1}>{t.trial.pageTitle}</h1><p>{t.trial.pageSub}</p></div></div>
      <Card><div className={s.formCard}><BookTrialForm sc={sc} onBooked={(l) => nav(`/leads/${l.id}`)} onCancel={() => nav(-1)} /></div></Card>
    </div>
  );
}
